import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Chess } from 'chess.js';
import { useSearchParams } from 'react-router-dom';
import { API_BASE } from '../api';
import { useDataCache } from '../DataCacheContext';
import InteractiveBoard, { svgPieceUrl, playSound, getSoundType } from './InteractiveBoard';

export default function PunishmentPanel({ c, pieceSet, boardTheme, setPieceSet, setBoardTheme }) {
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username') || '';
  const cache = useDataCache();

  // ── Data fetching ──
  const [loading, setLoading] = useState(true);
  const [fetchedPositions, setFetchedPositions] = useState([]);
  const [error, setError] = useState(null);
  const [fetchAttempt, setFetchAttempt] = useState(0);
  const [reviewMode, setReviewMode] = useState(false);

  // ── Training stats (gamification) ──
  const [trainingStats, setTrainingStats] = useState({ currentStreak: 0, bestStreak: 0, totalPunishments: 0, totalCorrect: 0 });

  useEffect(() => {
    if (!username) { setLoading(false); setError('No username found. Please log in first.'); return; }

    const modeParam = reviewMode ? '&mode=review' : '';
    const cacheKey = `punishment-positions:${username}${reviewMode ? ':review' : ''}`;

    const cached = cache.get(cacheKey);
    if (cached) { setFetchedPositions(cached); setLoading(false); setError(null); return; }

    setLoading(true); setError(null);
    fetch(`${API_BASE}/punishment-positions?username=${encodeURIComponent(username)}${modeParam}`)
      .then(r => { if (!r.ok) throw new Error(`Server error (${r.status})`); return r.json(); })
      .then(data => {
        const normalized = data.map(p => ({
          fen: p.fen,
          bestMove: p.bestMove || p.best_move,
          eval: p.eval || p.eval_estimate || '',
          evalDrop: p.evalDrop || p.eval_drop || 0,
          context: p.context || '',
          theme: p.theme || '',
          category: p.category || 'blunder',
          precedingMoves: p.precedingMoves || p.preceding_moves || [],
          opponentUsername: p.opponentUsername || p.opponent_username || '',
          gameUrl: p.gameUrl || p.game_url || '',
          openingName: p.openingName || p.opening_name || '',
          userActualMove: p.userActualMove || p.user_actual_move || '',
          playedAt: p.playedAt || p.played_at || '',
          dueForReview: p.dueForReview || p.due_for_review || false,
          solutionLine: p.solutionLine || p.solution_line || [],
          explanation: p.explanation || '',
          playerColor: p.playerColor || p.player_color || 'white',
        }));
        cache.set(cacheKey, normalized);
        setFetchedPositions(normalized);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [username, fetchAttempt, reviewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch training stats on mount
  useEffect(() => {
    if (!username) return;
    fetch(`${API_BASE}/training-stats?username=${encodeURIComponent(username)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setTrainingStats(data); })
      .catch(() => {});
  }, [username]);

  // ── Category filter (Mistakes vs Blunders) ──
  const [viewCategory, setViewCategory] = useState('all');
  // ── Color filter ──
  const [colorFilter, setColorFilter] = useState('white');
  const puzzles = useMemo(() => {
    let filtered = viewCategory === 'all' ? fetchedPositions : fetchedPositions.filter(p => p.category === viewCategory);
    if (colorFilter !== 'all') filtered = filtered.filter(p => p.playerColor === colorFilter);
    return filtered;
  }, [fetchedPositions, viewCategory, colorFilter]);

  // ── Puzzle state ──
  const [posIndex, setPosIndex] = useState(0);
  const [puzzleFeedback, setPuzzleFeedback] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [pStats, setPStats] = useState({ correct: 0, total: 0 });
  const [solutionStep, setSolutionStep] = useState(0); // tracks progress through multi-move solution line
  const [autoPlaying, setAutoPlaying] = useState(false); // true while auto-playing opponent response

  // ── Board state (same as free study) ──
  const [chess] = useState(() => new Chess());
  const [history, setHistory] = useState([]);
  const [moveIndex, setMoveIndex] = useState(0);
  const [positionsArr, setPositionsArr] = useState([]);
  const [flipped, setFlipped] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [lastMove, setLastMove] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showThreats, setShowThreats] = useState(false);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const [dragPiece, setDragPiece] = useState(null);
  const [hoveredSquare, setHoveredSquare] = useState(null);
  const [arrows, setArrows] = useState([]);
  const [highlightedSquares, setHighlightedSquares] = useState(new Set());
  const [pendingPromotion, setPendingPromotion] = useState(null);
  const [pgnCopied, setPgnCopied] = useState(false);

  const pos = puzzles.length > 0 ? puzzles[posIndex] : null;
  const [puzzleStartIdx, setPuzzleStartIdx] = useState(0);

  // Load puzzle position with preceding moves
  useEffect(() => {
    if (!pos) return;
    const preceding = pos.precedingMoves || [];
    const tempChess = new Chess();
    const positions = [tempChess.fen()];
    const hist = [];

    // Replay preceding moves to build history
    for (const san of preceding) {
      try {
        const result = tempChess.move(san);
        if (result) { hist.push(result); positions.push(tempChess.fen()); }
        else break;
      } catch { break; }
    }

    // Load puzzle FEN
    chess.load(pos.fen);
    const startIdx = positions.length - 1;
    setHistory(hist);
    setPositionsArr(positions);
    setMoveIndex(startIdx);
    setPuzzleStartIdx(startIdx);
    setSelectedSquare(null);
    setLegalMoves([]);
    setLastMove(null);
    setArrows([]);
    setHighlightedSquares(new Set());
    setPendingPromotion(null);
    setFlipped(pos.playerColor === 'black');
  }, [posIndex, pos?.fen]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentFen = positionsArr[moveIndex] || chess.fen();
  const boardChess = useMemo(() => { const bc = new Chess(); bc.load(currentFen); return bc; }, [currentFen]);

  const gameChess = useMemo(() => {
    const gc = new Chess();
    for (let i = 0; i < Math.min(moveIndex, history.length); i++) {
      try { gc.move(history[i].san); } catch { break; }
    }
    return gc;
  }, [history, moveIndex]);

  const squareName = useCallback((ri, ci) => {
    const f = flipped ? 7 - ci : ci;
    const r = flipped ? ri : 7 - ri;
    return 'abcdefgh'[f] + (r + 1);
  }, [flipped]);

  // ── Drag-and-drop ──
  const getSquareFromPoint = useCallback((clientX, clientY) => {
    // We need a ref but InteractiveBoard owns it now; use document query as fallback
    const boardEl = document.querySelector('[data-board-grid]');
    if (!boardEl) return null;
    const rect = boardEl.getBoundingClientRect();
    const x = clientX - rect.left, y = clientY - rect.top;
    if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return null;
    const col = Math.floor((x / rect.width) * 8), row = Math.floor((y / rect.height) * 8);
    const f = flipped ? 7 - col : col, r = flipped ? row : 7 - row;
    return 'abcdefgh'[f] + (r + 1);
  }, [flipped]);

  const handleDragStart = useCallback((ri, ci, e) => {
    if (puzzleFeedback || autoPlaying) return;
    if (moveIndex !== positionsArr.length - 1) return;
    const sq = squareName(ri, ci);
    const piece = boardChess.get(sq);
    if (!piece || piece.color !== boardChess.turn()) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragFrom(sq); setDragPiece(piece); setDragPos({ x: clientX, y: clientY });
    setSelectedSquare(sq);
    setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to));
  }, [moveIndex, positionsArr.length, squareName, boardChess, puzzleFeedback, autoPlaying]);

  const isPromotion = useCallback((from, to) => {
    const piece = boardChess.get(from);
    if (!piece || piece.type !== 'p') return false;
    return (piece.color === 'w' && to[1] === '8') || (piece.color === 'b' && to[1] === '1');
  }, [boardChess]);

  // Helper: record attempt for spaced repetition
  const _recordAttempt = useCallback((isCorrect, moveSan) => {
    if (!username || !pos) return;
    fetch(`${API_BASE}/punishment-attempt?username=${encodeURIComponent(username)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fen: pos.fen, correct: isCorrect, move_played: moveSan }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        fetch(`${API_BASE}/training-stats?username=${encodeURIComponent(username)}`)
          .then(r => r.ok ? r.json() : null)
          .then(s => { if (s) setTrainingStats(s); })
          .catch(() => {});
      })
      .catch(() => {});
    cache.set(`punishment-positions:${username}:review`, undefined);
  }, [username, pos, cache]); // eslint-disable-line react-hooks/exhaustive-deps

  const executeMove = useCallback((from, to, promotion = 'q') => {
    try {
      const tempChess = new Chess(currentFen);
      const result = tempChess.move({ from, to, promotion });
      if (result) {
        const newHistory = [...history.slice(0, moveIndex), result];
        const newPositions = [...positionsArr.slice(0, moveIndex + 1), tempChess.fen()];
        chess.load(tempChess.fen());
        setHistory(newHistory);
        setPositionsArr(newPositions);
        setMoveIndex(newPositions.length - 1);
        setLastMove({ from: result.from, to: result.to });
        setSelectedSquare(null);
        setLegalMoves([]);
        playSound(getSoundType(result));
        // ── Multi-move puzzle validation ──
        if (!puzzleFeedback && pos && !autoPlaying) {
          const line = pos.solutionLine || [];
          const userMoveIdx = solutionStep * 2; // user moves are at even indices in solution line
          const expectedMove = userMoveIdx === 0 ? pos.bestMove : (line[userMoveIdx] || null);

          if (expectedMove && moveIndex >= puzzleStartIdx) {
            const isCorrect = result.san === expectedMove;

            if (!isCorrect) {
              // Wrong move — end puzzle
              setPuzzleFeedback({ correct: false, movePlayed: result.san, bestMove: expectedMove, step: solutionStep + 1, totalSteps: Math.ceil(line.length / 2) || 1 });
              setPStats(s => ({ correct: s.correct, total: s.total + 1 }));
              _recordAttempt(false, result.san);
            } else if (line.length > 0 && userMoveIdx + 2 < line.length) {
              // Correct, but more moves needed — auto-play opponent's response
              const opponentMove = line[userMoveIdx + 1];
              if (opponentMove) {
                setSolutionStep(s => s + 1);
                setAutoPlaying(true);
                setTimeout(() => {
                  try {
                    const tempChess = new Chess(chess.fen());
                    const oppResult = tempChess.move(opponentMove);
                    if (oppResult) {
                      const nh = [...history.slice(0, moveIndex + 1), oppResult];
                      const np = [...positionsArr.slice(0, moveIndex + 2), tempChess.fen()];
                      chess.load(tempChess.fen());
                      setHistory(nh);
                      setPositionsArr(np);
                      setMoveIndex(np.length - 1);
                      setLastMove({ from: oppResult.from, to: oppResult.to });
                      playSound(getSoundType(oppResult));
                    }
                  } catch { /* invalid opponent move */ }
                  setAutoPlaying(false);
                  setSelectedSquare(null);
                  setLegalMoves([]);
                }, 600);
              }
            } else {
              // Correct and final move — puzzle complete!
              const totalSteps = Math.ceil(line.length / 2) || 1;
              setPuzzleFeedback({ correct: true, movePlayed: result.san, bestMove: expectedMove, step: totalSteps, totalSteps });
              setPStats(s => ({ correct: s.correct + 1, total: s.total + 1 }));
              _recordAttempt(true, result.san);
            }
          }
        }
        return true;
      }
    } catch { /* invalid */ }
    return false;
  }, [currentFen, chess, history, positionsArr, moveIndex, puzzleFeedback, pos, puzzleStartIdx, solutionStep, autoPlaying, _recordAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePromotionChoice = useCallback((pieceType) => {
    if (!pendingPromotion) return;
    executeMove(pendingPromotion.from, pendingPromotion.to, pieceType);
    setPendingPromotion(null);
  }, [pendingPromotion, executeMove]);

  useEffect(() => {
    if (!dragFrom) return;
    const onMove = (e) => { const cX = e.touches ? e.touches[0].clientX : e.clientX; const cY = e.touches ? e.touches[0].clientY : e.clientY; setDragPos({ x: cX, y: cY }); };
    const onEnd = (e) => {
      const cX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX; const cY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
      const dropSq = getSquareFromPoint(cX, cY);
      if (dropSq && dropSq !== dragFrom) {
        if (isPromotion(dragFrom, dropSq)) { const piece = boardChess.get(dragFrom); setPendingPromotion({ from: dragFrom, to: dropSq, color: piece.color }); setDragFrom(null); setDragPiece(null); setDragPos(null); return; }
        executeMove(dragFrom, dropSq);
      }
      setDragFrom(null); setDragPiece(null); setDragPos(null);
    };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false }); window.addEventListener('touchend', onEnd);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onEnd); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onEnd); };
  }, [dragFrom, getSquareFromPoint, isPromotion, executeMove, boardChess]);

  const handleSquareClick = useCallback((ri, ci) => {
    if (puzzleFeedback || autoPlaying) return;
    if (moveIndex !== positionsArr.length - 1) return;
    const sq = squareName(ri, ci);
    const piece = boardChess.get(sq);
    if (selectedSquare) {
      const legal = boardChess.moves({ square: selectedSquare, verbose: true });
      if (legal.some(m => m.to === sq)) {
        if (isPromotion(selectedSquare, sq)) { setPendingPromotion({ from: selectedSquare, to: sq, color: boardChess.get(selectedSquare).color }); return; }
        executeMove(selectedSquare, sq); return;
      }
      if (piece && piece.color === boardChess.turn()) { setSelectedSquare(sq); setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to)); return; }
      setSelectedSquare(null); setLegalMoves([]); return;
    }
    if (piece && piece.color === boardChess.turn()) { setSelectedSquare(sq); setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to)); }
  }, [selectedSquare, boardChess, moveIndex, positionsArr.length, squareName, isPromotion, executeMove, puzzleFeedback, autoPlaying]);

  const goTo = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(idx, positionsArr.length - 1));
    setMoveIndex(clamped); setSelectedSquare(null); setLegalMoves([]);
    if (clamped > 0 && history[clamped - 1]) setLastMove({ from: history[clamped - 1].from, to: history[clamped - 1].to }); else setLastMove(null);
  }, [positionsArr, history]);

  const handleUndo = useCallback(() => { // eslint-disable-line no-unused-vars
    if (moveIndex <= 0) return;
    const nh = history.slice(0, moveIndex - 1), np = positionsArr.slice(0, moveIndex);
    chess.load(np[np.length - 1]); setHistory(nh); setPositionsArr(np); setMoveIndex(np.length - 1);
    setSelectedSquare(null); setLegalMoves([]);
    if (nh.length > 0) setLastMove({ from: nh[nh.length - 1].from, to: nh[nh.length - 1].to }); else setLastMove(null);
    if (moveIndex === puzzleStartIdx + 1) setPuzzleFeedback(null);
  }, [chess, history, positionsArr, moveIndex, puzzleStartIdx]);

  // ── Derived data ──
  const turnColor = boardChess.turn();
  const fullMoveNumber = Math.floor(moveIndex / 2) + 1;
  const isCheck = boardChess.isCheck();
  const isCheckmate = boardChess.isCheckmate();
  const isStalemate = boardChess.isStalemate();
  const isThreefoldRepetition = gameChess.isThreefoldRepetition();
  const isInsufficientMaterial = boardChess.isInsufficientMaterial();
  const isFiftyMoveRule = gameChess.isDraw() && !isStalemate && !isInsufficientMaterial && !isThreefoldRepetition;
  const isDraw = isStalemate || isThreefoldRepetition || isInsufficientMaterial || isFiftyMoveRule;
  const isGameOver = isCheckmate || isDraw;
  const playerColor = flipped ? 'b' : 'w';

  const materialCount = useMemo(() => {
    const values = { p:1,n:3,b:3,r:5,q:9 };
    const pieces = { w:{p:0,n:0,b:0,r:0,q:0}, b:{p:0,n:0,b:0,r:0,q:0} };
    const start = { p:8,n:2,b:2,r:2,q:1 };
    for (const row of boardChess.board()) for (const sq of row) if (sq && sq.type !== 'k') pieces[sq.color][sq.type]++;
    const captured = { w:{}, b:{} };
    for (const t of ['q','r','b','n','p']) { captured.w[t] = start[t] - pieces.b[t]; captured.b[t] = start[t] - pieces.w[t]; }
    let wM=0,bM=0; for (const t of ['p','n','b','r','q']) { wM += pieces.w[t]*values[t]; bM += pieces.b[t]*values[t]; }
    return { pieces, captured, advantage: wM-bM };
  }, [currentFen]); // eslint-disable-line react-hooks/exhaustive-deps

  const detectedOpening = pos?.openingName || (history.length > 0 ? 'Starting Position' : null);

  const evalPercent = useMemo(() => Math.max(5, Math.min(95, 50 + materialCount.advantage * 5)), [materialCount.advantage]);

  const heatmapData = useMemo(() => {
    if (!showHeatmap) return null;
    const map = {}; for (const f of 'abcdefgh') for (const r of '12345678') map[f+r] = 0;
    for (const sq of Object.keys(map)) { const piece = boardChess.get(sq); if (!piece || piece.color !== playerColor) continue; try { for (const m of boardChess.moves({ square: sq, verbose: true })) map[m.to]++; } catch {} }
    return map;
  }, [currentFen, showHeatmap, playerColor]); // eslint-disable-line react-hooks/exhaustive-deps

  const threatData = useMemo(() => {
    if (!showThreats) return null;
    const threats = {}; const opp = playerColor === 'w' ? 'b' : 'w';
    for (const f of 'abcdefgh') for (const r of '12345678') { const sq = f+r; const piece = boardChess.get(sq); if (!piece || piece.color !== playerColor) continue; if (boardChess.isAttacked(sq, opp)) threats[sq] = { attacked: true, defended: boardChess.isAttacked(sq, playerColor), piece }; }
    return threats;
  }, [currentFen, showThreats, playerColor]); // eslint-disable-line react-hooks/exhaustive-deps

  const classifyMove = useCallback((move) => {
    const tags = [];
    if (move.san === 'O-O' || move.san === 'O-O-O') { tags.push('Castle'); return tags; }
    if (move.san.includes('+')) tags.push('Check'); if (move.san.includes('#')) { tags.push('Checkmate'); return tags; }
    if (move.captured) tags.push('Capture');
    if (move.piece === 'p' && (move.to[1] === '8' || move.to[1] === '1')) tags.push('Promotion');
    if (move.piece !== 'p' && !move.captured && moveIndex <= 20 && ['n','b'].includes(move.piece)) tags.push('Development');
    if (move.piece === 'p') tags.push('Pawn push');
    if (tags.length === 0) tags.push('Quiet');
    return tags;
  }, [moveIndex]);

  const generatePGN = useCallback(() => {
    const moves = history.slice(0, moveIndex).map((m, i) => (i % 2 === 0 ? `${Math.floor(i/2)+1}. ${m.san}` : m.san)).join(' ');
    const pgn = `[Event "Punishment Training"]\n[Date "${new Date().toISOString().slice(0,10)}"]\n[White "?"]\n[Black "?"]\n[Result "*"]\n\n${moves} *`;
    navigator.clipboard.writeText(pgn); return pgn;
  }, [history, moveIndex]);

  const movePairs = []; for (let i = 0; i < history.length; i += 2) movePairs.push({ num: Math.floor(i/2)+1, white: history[i], black: history[i+1]||null, whiteIdx: i+1, blackIdx: i+2 });
  const nextPosition = () => { setPosIndex((posIndex + 1) % puzzles.length); setPuzzleFeedback(null); setShowHint(false); setSolutionStep(0); };

  const cardSx = { px:2.5, py:2, borderRadius:'16px', bgcolor:`${c.textMuted}06`, border:`1px solid ${c.textMuted}10` };
  const labelSx = { color:c.textMuted, fontSize:'0.8rem', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', mb:1.5 };

  // ═══ LOADING STATE ═══
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${c.accent}20`, borderTopColor: c.accent, animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 2 }}>Analyzing opponent mistakes...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: c.accent, fontSize: '1rem', fontWeight: 700, mb: 1 }}>Could not load punishment positions</Typography>
        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mb: 2 }}>{error}</Typography>
        <Box onClick={() => setFetchAttempt(a => a + 1)} sx={{
          display: 'inline-flex', alignItems: 'center', gap: 1, px: 2.5, py: 1,
          borderRadius: '10px', cursor: 'pointer', bgcolor: `${c.accent}12`, border: `1.5px solid ${c.accent}30`,
          transition: 'all 0.15s ease', '&:hover': { bgcolor: `${c.accent}20` },
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="2" strokeLinecap="round"><path d="M23 4v6h-6" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" /></svg>
          <Typography sx={{ color: c.accent, fontSize: '0.85rem', fontWeight: 700 }}>Retry</Typography>
        </Box>
      </Box>
    );
  }

  if (puzzles.length === 0) {
    return (
      <Box sx={{ ...cardSx, textAlign: 'center', py: 8 }}>
        <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>&#9876;</Typography>
        <Typography sx={{ color: c.textDark, fontSize: '1.15rem', fontWeight: 700, mb: 1 }}>No punishment positions found</Typography>
        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.7 }}>Play more games on Chess.com and sync your data to discover opponent mistakes you can learn to punish.</Typography>
      </Box>
    );
  }
  const accuracy = pStats.total > 0 ? Math.round((pStats.correct / pStats.total) * 100) : 0;

  return (
    <Box sx={{ display:'flex', flexDirection:{ xs:'column', lg:'row' }, gap:3, alignItems:{ lg:'flex-start' } }}>

      {/* ═══ LEFT: Filter panel ═══ */}
      <Box sx={{ flex:{ xs:'1 1 100%', lg:'0 0 280px' }, maxWidth:{ xs:'100%', lg:280 }, minWidth:0 }}>
        <Box sx={{ minWidth:0, overflow:'auto', maxHeight:{ lg:'calc(100vh - 160px)' }, pr:{ lg:2 } }}>
          <Typography sx={{ color:c.textDark, fontSize:'1.3rem', fontWeight:800, mb:0.5 }}>Punish Mistakes</Typography>
          <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', mb:3, lineHeight:1.6 }}>
            Find the strongest punishment for your opponent's mistakes.
          </Typography>

          {/* Color toggle — matches OpeningTrainer */}
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:2.5 }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:0.5, p:0.5, borderRadius:'50px', bgcolor:`${c.textMuted}08` }}>
              <Box onClick={() => { if (colorFilter !== 'white') { setColorFilter('white'); setPosIndex(0); setPuzzleFeedback(null); setShowHint(false); } }} sx={{ width:40, height:40, borderRadius:'50%', bgcolor:'#ECE2DB', cursor:'pointer', transition:'all 0.15s ease', opacity: colorFilter === 'white' ? 1 : 0.3, transform: colorFilter === 'white' ? 'scale(1)' : 'scale(0.85)', '&:hover':{ opacity: colorFilter === 'white' ? 1 : 0.6 } }} />
              <Box onClick={() => { setColorFilter(colorFilter === 'white' ? 'black' : 'white'); setPosIndex(0); setPuzzleFeedback(null); setShowHint(false); }} sx={{ cursor:'pointer', display:'flex', px:0.3, transition:'all 0.15s ease', '&:hover':{ transform:'scale(1.2)' } }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c.textMuted} strokeWidth="2" strokeLinecap="round"><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 014-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 01-4 4H3" /></svg>
              </Box>
              <Box onClick={() => { if (colorFilter !== 'black') { setColorFilter('black'); setPosIndex(0); setPuzzleFeedback(null); setShowHint(false); } }} sx={{ width:40, height:40, borderRadius:'50%', bgcolor:'#111115', cursor:'pointer', transition:'all 0.15s ease', opacity: colorFilter === 'black' ? 1 : 0.3, transform: colorFilter === 'black' ? 'scale(1)' : 'scale(0.85)', '&:hover':{ opacity: colorFilter === 'black' ? 1 : 0.6 } }} />
            </Box>
            <Typography sx={{ color:c.textDark, fontSize:'1.1rem', fontWeight:700 }}>As {colorFilter}</Typography>
          </Box>

          {/* Category filter */}
          <Box sx={{ display:'flex', gap:0.5, p:0.5, borderRadius:'12px', bgcolor:`${c.textMuted}08`, mb:2.5 }}>
            {[{ key:'all', label:'All' }, { key:'mistake', label:'Mistakes' }, { key:'blunder', label:'Blunders' }].map(opt => (
              <Box key={opt.key} onClick={() => { setViewCategory(opt.key); setPosIndex(0); setPuzzleFeedback(null); setShowHint(false); }} sx={{
                flex:1, py:1.2, borderRadius:'10px', cursor:'pointer', textAlign:'center',
                bgcolor: viewCategory === opt.key ? `${c.accent}15` : 'transparent',
                border: `1.5px solid ${viewCategory === opt.key ? c.accent + '30' : 'transparent'}`,
                transition:'all 0.15s ease',
                '&:hover':{ bgcolor: viewCategory === opt.key ? `${c.accent}15` : `${c.textMuted}06` },
              }}>
                <Typography sx={{ color: viewCategory === opt.key ? c.accent : c.textMuted, fontSize:'0.9rem', fontWeight:700 }}>{opt.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Position list */}
          <Typography sx={{ color:c.textMuted, fontSize:'0.9rem', fontWeight:600, mb:1 }}>{puzzles.length} position{puzzles.length !== 1 ? 's' : ''}</Typography>
          <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
            {puzzles.map((p, i) => {
              const isActive = posIndex === i;
              return (
                <Box key={i} onClick={() => { setPosIndex(i); setPuzzleFeedback(null); setShowHint(false); setSolutionStep(0); }} sx={{
                  px:2, py:1.5, borderRadius:'12px', cursor:'pointer', transition:'all 0.15s ease',
                  bgcolor: isActive ? `${c.accent}10` : `${c.textMuted}06`,
                  border: `1px solid ${isActive ? c.accent + '25' : c.textMuted + '10'}`,
                  '&:hover':{ bgcolor:`${c.accent}08`, borderColor:`${c.accent}20` },
                }}>
                  <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <Typography sx={{ color: isActive ? c.accent : c.textDark, fontSize:'1.05rem', fontWeight:700, flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {p.openingName || `Position ${i + 1}`}
                    </Typography>
                    <Box sx={{ px:1.2, py:0.3, borderRadius:'8px', ml:1, flexShrink:0, bgcolor: p.category === 'blunder' ? '#E4446E15' : '#DE734515' }}>
                      <Typography sx={{ color: p.category === 'blunder' ? '#E4446E' : '#DE7345', fontSize:'0.85rem', fontWeight:800 }}>
                        {p.eval}
                      </Typography>
                    </Box>
                  </Box>
                  {p.opponentUsername && (
                    <Typography sx={{ color:c.textMuted, fontSize:'0.88rem', mt:0.3 }}>vs {p.opponentUsername}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ═══ CENTER: Board ═══ */}
      <Box sx={{ flexShrink:0, position:'relative' }}>
      <InteractiveBoard
        c={c}
        fen={currentFen}
        flipped={flipped}
        pieceSet={pieceSet}
        boardTheme={boardTheme}
        selectedSquare={selectedSquare}
        legalMoves={legalMoves}
        lastMove={lastMove}
        onSquareClick={(ri, ci) => handleSquareClick(ri, ci)}
        onDragStart={handleDragStart}
        onFlip={() => setFlipped(f => !f)}
        onGoTo={goTo}
        setArrows={setArrows}
        setHighlightedSquares={setHighlightedSquares}
        setPieceSet={setPieceSet}
        setBoardTheme={setBoardTheme}
        arrows={arrows}
        highlightedSquares={highlightedSquares}
        dragFrom={dragFrom}
        dragPos={dragPos}
        dragPiece={dragPiece}
        hoveredSquare={hoveredSquare}
        setHoveredSquare={setHoveredSquare}
        pendingPromotion={pendingPromotion}
        onPromotionChoice={handlePromotionChoice}
        setPendingPromotion={setPendingPromotion}
        positions={positionsArr}
        moveIndex={moveIndex}
        heatmapData={heatmapData}
        threatData={threatData}
        showEvalBar={true}
        evalPercent={evalPercent}
        materialAdvantage={materialCount.advantage}
      />
      </Box>

      {/* ═══ RIGHT: Info panel ═══ */}
      <Box sx={{ flex:'0 0 280px', display:'flex', flexDirection:'column', gap:2, minWidth:0 }}>

        {/* ── PUNISHMENT CARDS ── */}
        <Box sx={{ ...cardSx, bgcolor:`${c.accent}06`, border:`1px solid ${c.accent}12` }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.2, mb:1.5 }}>
            <Box sx={{ px:1.5, py:0.5, borderRadius:'10px', bgcolor: pos.category === 'mistake' ? '#DE734515' : '#E4446E15', border: `1px solid ${pos.category === 'mistake' ? '#DE734530' : '#E4446E30'}` }}><Typography sx={{ color: pos.category === 'mistake' ? '#DE7345' : '#E4446E', fontSize:'0.95rem', fontWeight:800 }}>{pos.category === 'mistake' ? 'Mistake' : 'Blunder!'}</Typography></Box>
            <Box sx={{ px:1.5, py:0.5, borderRadius:'10px', bgcolor:`${c.textMuted}08`, border:`1px solid ${c.textMuted}15` }}><Typography sx={{ color:c.textMuted, fontSize:'0.9rem', fontWeight:600 }}>{pos.theme}</Typography></Box>
            {pos.eval && <Box sx={{ px:1.5, py:0.5, borderRadius:'10px', bgcolor:`${c.blue}12`, border:`1px solid ${c.blue}25` }}><Typography sx={{ color:c.blue, fontSize:'0.9rem', fontWeight:700 }}>{pos.eval}</Typography></Box>}
            {pos.dueForReview && <Box sx={{ px:1.5, py:0.5, borderRadius:'10px', bgcolor:`${c.accent}12`, border:`1px solid ${c.accent}25` }}><Typography sx={{ color:c.accent, fontSize:'0.8rem', fontWeight:700 }}>Due for review</Typography></Box>}
          </Box>
          <Typography sx={{ color:c.textDark, fontSize:'1.25rem', fontWeight:800 }}>Punish the mistake!</Typography>
          <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', lineHeight:1.7, mt:0.5 }}>{pos.context}</Typography>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mt:1.5 }}>
            <Typography sx={{ color:c.textMuted, fontSize:'0.9rem', opacity:0.6 }}>Position {posIndex+1} of {puzzles.length}</Typography>
            {!puzzleFeedback && pos.solutionLine && pos.solutionLine.length > 2 && (
              <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                {Array.from({ length: Math.ceil(pos.solutionLine.length / 2) }).map((_, idx) => (
                  <Box key={idx} sx={{ width:8, height:8, borderRadius:'50%', bgcolor: idx < solutionStep ? c.blue : (idx === solutionStep ? c.accent : `${c.textMuted}30`), transition:'all 0.2s ease' }} />
                ))}
                <Typography sx={{ color:c.textMuted, fontSize:'0.8rem', ml:0.5 }}>Move {solutionStep + 1}/{Math.ceil(pos.solutionLine.length / 2)}</Typography>
              </Box>
            )}
          </Box>
        </Box>

        {puzzleFeedback && (
          <Box sx={{ ...cardSx, bgcolor:puzzleFeedback.correct?`${c.blue}08`:'#E4446E08', border:`1px solid ${puzzleFeedback.correct?`${c.blue}25`:'#E4446E25'}` }}>
            <Typography sx={{ color:puzzleFeedback.correct?c.blue:'#E4446E', fontSize:'1.2rem', fontWeight:800, mb:0.5 }}>
              {puzzleFeedback.correct
                ? (puzzleFeedback.totalSteps > 1 ? `Full punishment line executed! (${puzzleFeedback.totalSteps} moves)` : 'Maximum punishment!')
                : (puzzleFeedback.totalSteps > 1 ? `Wrong on move ${puzzleFeedback.step} of ${puzzleFeedback.totalSteps}` : 'Not the strongest')}
            </Typography>
            {!puzzleFeedback.correct && <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', lineHeight:1.7 }}>You played <strong style={{ color:c.textDark }}>{puzzleFeedback.movePlayed}</strong>. The strongest was <strong style={{ color:c.blue }}>{puzzleFeedback.bestMove}</strong>.</Typography>}
            <Box onClick={nextPosition} sx={{ mt:2, px:3, py:1.2, borderRadius:'12px', cursor:'pointer', bgcolor:`${c.accent}12`, border:`1.5px solid ${c.accent}30`, display:'inline-flex', alignItems:'center', gap:1, transition:'all 0.15s ease', '&:hover':{ bgcolor:`${c.accent}20` } }}>
              <Typography sx={{ color:c.accent, fontSize:'1.05rem', fontWeight:700 }}>Next Position</Typography>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
            </Box>
          </Box>
        )}

        {/* ── COMPARATIVE ANALYSIS ── */}
        {puzzleFeedback && pos.userActualMove && (
          <Box sx={cardSx}>
            <Typography sx={labelSx}>In Your Game</Typography>
            <Box sx={{ display:'flex', flexDirection:'column', gap:1.5 }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:c.textMuted, flexShrink:0 }} />
                <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', lineHeight:1.6 }}>
                  You played <strong style={{ color:c.textDark }}>{pos.userActualMove}</strong>
                  {pos.userActualMove === pos.bestMove
                    ? <Box component="span" sx={{ ml:1, px:1, py:0.2, borderRadius:'6px', bgcolor:`${c.blue}15`, border:`1px solid ${c.blue}25`, fontSize:'0.8rem', fontWeight:700, color:c.blue }}>Correct in game</Box>
                    : <Box component="span" sx={{ ml:1, px:1, py:0.2, borderRadius:'6px', bgcolor:'#E4446E12', border:'1px solid #E4446E25', fontSize:'0.8rem', fontWeight:700, color:'#E4446E' }}>Missed</Box>
                  }
                </Typography>
              </Box>
              <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:c.blue, flexShrink:0 }} />
                <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', lineHeight:1.6 }}>
                  Best punishment: <strong style={{ color:c.blue }}>{pos.bestMove}</strong>
                  {pos.eval && <Box component="span" sx={{ ml:1, color:c.blue, fontSize:'0.9rem', fontWeight:600 }}>({pos.eval})</Box>}
                </Typography>
              </Box>
              {pos.opponentUsername && (
                <Typography sx={{ color:c.textMuted, fontSize:'0.85rem', opacity:0.6, mt:0.5 }}>
                  vs {pos.opponentUsername}{pos.openingName ? ` \u2022 ${pos.openingName}` : ''}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* ── EXPLANATION PANEL ── */}
        {puzzleFeedback && pos.explanation && (
          <Box sx={{ ...cardSx, bgcolor:`${c.blue}06`, border:`1px solid ${c.blue}10` }}>
            <Typography sx={{ ...labelSx, color:c.blue }}>Why This Works</Typography>
            <Typography sx={{ color:c.textDark, fontSize:'1.05rem', lineHeight:1.7 }}>{pos.explanation}</Typography>
            {pos.solutionLine && pos.solutionLine.length > 1 && (
              <Box sx={{ mt:1.5, pt:1.5, borderTop:`1px solid ${c.textMuted}10` }}>
                <Typography sx={{ color:c.textMuted, fontSize:'0.85rem', fontWeight:600, mb:0.8 }}>Full punishment line:</Typography>
                <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.8 }}>
                  {pos.solutionLine.map((move, idx) => (
                    <Box key={idx} sx={{ px:1.2, py:0.4, borderRadius:'8px', bgcolor: idx % 2 === 0 ? `${c.blue}12` : `${c.textMuted}08`, border:`1px solid ${idx % 2 === 0 ? c.blue + '20' : c.textMuted + '12'}` }}>
                      <Typography sx={{ color: idx % 2 === 0 ? c.blue : c.textMuted, fontSize:'0.9rem', fontWeight:700, fontFamily:'monospace' }}>
                        {idx % 2 === 0 ? `${Math.floor(idx/2)+1}. ` : ''}{move}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {!puzzleFeedback && (
          <Box sx={cardSx}>
            {showHint ? (<><Typography sx={labelSx}>Hint</Typography><Typography sx={{ color:c.textDark, fontSize:'1.05rem', lineHeight:1.7 }}>{pos.context}</Typography></>) : (
              <Box onClick={() => setShowHint(true)} sx={{ cursor:'pointer', display:'flex', alignItems:'center', gap:1.2 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c.textMuted} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', fontWeight:600 }}>Show hint</Typography>
              </Box>
            )}
          </Box>
        )}

        <Box sx={cardSx}>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1.5 }}>
            <Typography sx={{ ...labelSx, mb:0 }}>Session Stats</Typography>
            {/* Review Mode Toggle */}
            <Box onClick={() => { setReviewMode(r => !r); setPosIndex(0); setPuzzleFeedback(null); }} sx={{
              display:'inline-flex', alignItems:'center', gap:0.8, px:1.5, py:0.5, borderRadius:'8px', cursor:'pointer',
              bgcolor: reviewMode ? `${c.accent}15` : `${c.textMuted}08`,
              border: `1px solid ${reviewMode ? c.accent + '30' : c.textMuted + '12'}`,
              transition:'all 0.15s ease',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={reviewMode ? c.accent : c.textMuted} strokeWidth="2" strokeLinecap="round"><path d="M23 4v6h-6" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" /></svg>
              <Typography sx={{ color: reviewMode ? c.accent : c.textMuted, fontSize:'0.78rem', fontWeight:700 }}>Review</Typography>
            </Box>
          </Box>
          <Box sx={{ display:'flex', gap:3, flexWrap:'wrap' }}>
            <Box><Typography sx={{ color:c.textDark, fontSize:'1.8rem', fontWeight:800 }}>{pStats.total}</Typography><Typography sx={{ color:c.textMuted, fontSize:'0.9rem' }}>Attempted</Typography></Box>
            <Box><Typography sx={{ color:c.blue, fontSize:'1.8rem', fontWeight:800 }}>{pStats.correct}</Typography><Typography sx={{ color:c.textMuted, fontSize:'0.9rem' }}>Punished</Typography></Box>
            <Box><Typography sx={{ color:c.accent, fontSize:'1.8rem', fontWeight:800 }}>{accuracy}%</Typography><Typography sx={{ color:c.textMuted, fontSize:'0.9rem' }}>Kill rate</Typography></Box>
          </Box>
          {/* Gamification: Streaks & Lifetime */}
          {(trainingStats.totalPunishments > 0 || trainingStats.currentStreak > 0) && (
            <Box sx={{ mt:2, pt:2, borderTop:`1px solid ${c.textMuted}10` }}>
              <Box sx={{ display:'flex', gap:3, flexWrap:'wrap' }}>
                <Box>
                  <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                    <Typography sx={{ color: trainingStats.currentStreak >= 3 ? '#DE7345' : c.textDark, fontSize:'1.4rem', fontWeight:800 }}>{trainingStats.currentStreak}</Typography>
                    {trainingStats.currentStreak >= 3 && <Typography sx={{ fontSize:'1.1rem' }}>&#128293;</Typography>}
                  </Box>
                  <Typography sx={{ color:c.textMuted, fontSize:'0.8rem' }}>Streak</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color:c.accent, fontSize:'1.4rem', fontWeight:800 }}>{trainingStats.bestStreak}</Typography>
                  <Typography sx={{ color:c.textMuted, fontSize:'0.8rem' }}>Best streak</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color:c.textDark, fontSize:'1.4rem', fontWeight:800 }}>{trainingStats.totalPunishments}</Typography>
                  <Typography sx={{ color:c.textMuted, fontSize:'0.8rem' }}>Lifetime</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color:c.blue, fontSize:'1.4rem', fontWeight:800 }}>{trainingStats.totalPunishments > 0 ? Math.round((trainingStats.totalCorrect / trainingStats.totalPunishments) * 100) : 0}%</Typography>
                  <Typography sx={{ color:c.textMuted, fontSize:'0.8rem' }}>All-time acc.</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* ── STANDARD FREE STUDY CARDS ── */}
        <Box sx={{ ...cardSx, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
            <Box sx={{ width:20, height:20, borderRadius:'50%', bgcolor:turnColor==='w'?'#ECE2DB':'#111115' }} />
            <Typography sx={{ color:c.textDark, fontSize:'1.1rem', fontWeight:700 }}>
              {isCheckmate?(turnColor==='w'?'Black wins':'White wins'):isStalemate?'Stalemate':isDraw?'Draw':isCheck?(turnColor==='w'?'White in check':'Black in check'):turnColor==='w'?'White to move':'Black to move'}
            </Typography>
          </Box>
          <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', fontWeight:600 }}>Move {fullMoveNumber}</Typography>
        </Box>

        {detectedOpening && <Box sx={{ ...cardSx, bgcolor:`${c.accent}06`, border:`1px solid ${c.accent}12` }}><Typography sx={{ ...labelSx, mb:0.5 }}>Opening</Typography><Typography sx={{ color:c.textDark, fontSize:'1.25rem', fontWeight:800 }}>{detectedOpening}</Typography></Box>}

        <Box sx={cardSx}>
          <Typography sx={labelSx}>Captured Pieces</Typography>
          <Box sx={{ display:'flex', alignItems:'center', gap:0.8, mb:0.8 }}>
            <Box sx={{ width:14, height:14, borderRadius:'50%', bgcolor:'#ECE2DB', flexShrink:0 }} />
            <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.3, minHeight:28, alignItems:'center' }}>
              {['q','r','b','n','p'].map(type => Array(materialCount.captured.w[type]).fill(null).map((_,i) => <Box key={`wc-${type}-${i}`} component="img" src={svgPieceUrl(pieceSet,`b${type.toUpperCase()}`)} alt="" sx={{ width:26, height:26, opacity:0.7 }} />))}
              {materialCount.advantage>0 && <Typography sx={{ color:c.textMuted, fontSize:'0.95rem', fontWeight:700, ml:0.5 }}>+{materialCount.advantage}</Typography>}
            </Box>
          </Box>
          <Box sx={{ display:'flex', alignItems:'center', gap:0.8 }}>
            <Box sx={{ width:14, height:14, borderRadius:'50%', bgcolor:'#111115', flexShrink:0 }} />
            <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.3, minHeight:28, alignItems:'center' }}>
              {['q','r','b','n','p'].map(type => Array(materialCount.captured.b[type]).fill(null).map((_,i) => <Box key={`bc-${type}-${i}`} component="img" src={svgPieceUrl(pieceSet,`w${type.toUpperCase()}`)} alt="" sx={{ width:26, height:26, opacity:0.7 }} />))}
              {materialCount.advantage<0 && <Typography sx={{ color:c.textMuted, fontSize:'0.95rem', fontWeight:700, ml:0.5 }}>+{Math.abs(materialCount.advantage)}</Typography>}
            </Box>
          </Box>
        </Box>

        <Box sx={{ ...cardSx, maxHeight:{ xs:180, md:260 }, overflow:'auto' }}>
          <Typography sx={labelSx}>Moves</Typography>
          {movePairs.length===0 ? <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', fontStyle:'italic' }}>Play a move to begin...</Typography> : (
            <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.5, alignItems:'center' }}>
              {movePairs.map((pair) => (
                <React.Fragment key={pair.num}>
                  <Typography sx={{ color:c.textMuted, fontSize:'0.95rem', fontWeight:600, mr:0.2 }}>{pair.num}.</Typography>
                  <Box onClick={() => { goTo(pair.whiteIdx); playSound('move'); }} sx={{ px:1, py:0.3, borderRadius:'6px', cursor:'pointer', bgcolor:moveIndex===pair.whiteIdx?`${c.accent}25`:'transparent', '&:hover':{ bgcolor:`${c.accent}12` } }}>
                    <Typography sx={{ color:moveIndex===pair.whiteIdx?c.accent:c.textDark, fontSize:'1rem', fontWeight:moveIndex===pair.whiteIdx?700:500 }}>{pair.white.san}</Typography>
                  </Box>
                  {pair.black && <Box onClick={() => { goTo(pair.blackIdx); playSound('move'); }} sx={{ px:1, py:0.3, borderRadius:'6px', cursor:'pointer', bgcolor:moveIndex===pair.blackIdx?`${c.accent}25`:'transparent', '&:hover':{ bgcolor:`${c.accent}12` } }}>
                    <Typography sx={{ color:moveIndex===pair.blackIdx?c.accent:c.textDark, fontSize:'1rem', fontWeight:moveIndex===pair.blackIdx?700:500 }}>{pair.black.san}</Typography>
                  </Box>}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>

        {history.length>0 && <Box onClick={() => { generatePGN(); setPgnCopied(true); setTimeout(() => setPgnCopied(false),2000); }} sx={{ ...cardSx, cursor:'pointer', display:'flex', alignItems:'center', gap:1.2, transition:'all 0.15s ease', bgcolor:pgnCopied?`${c.accent}15`:cardSx.bgcolor, border:pgnCopied?`1px solid ${c.accent}`:cardSx.border, '&:hover':{ bgcolor:`${c.textMuted}10` } }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={pgnCopied?c.accent:c.textMuted} strokeWidth="2" strokeLinecap="round">{pgnCopied?<polyline points="20 6 9 17 4 12" />:<><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></>}</svg>
          <Typography sx={{ color:pgnCopied?c.accent:c.textMuted, fontSize:'1rem', fontWeight:600 }}>{pgnCopied?'PGN Copied!':'Copy PGN'}</Typography>
        </Box>}

        <Box sx={{ display:'flex', gap:1 }}>
          <Box onClick={() => setShowHeatmap(h=>!h)} sx={{ ...cardSx, flex:1, cursor:'pointer', display:'flex', alignItems:'center', gap:1, border:showHeatmap?`1px solid ${c.accent}`:cardSx.border, bgcolor:showHeatmap?`${c.accent}08`:cardSx.bgcolor, '&:hover':{ bgcolor:`${c.textMuted}10` } }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showHeatmap?c.accent:c.textMuted} strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" opacity="0.3" /><rect x="14" y="3" width="7" height="7" opacity="0.6" /><rect x="3" y="14" width="7" height="7" opacity="0.6" /><rect x="14" y="14" width="7" height="7" /></svg>
            <Typography sx={{ color:showHeatmap?c.accent:c.textMuted, fontSize:'0.95rem', fontWeight:600 }}>Heatmap</Typography>
          </Box>
          <Box onClick={() => setShowThreats(t=>!t)} sx={{ ...cardSx, flex:1, cursor:'pointer', display:'flex', alignItems:'center', gap:1, border:showThreats?'1px solid #E4446E':cardSx.border, bgcolor:showThreats?'#E4446E08':cardSx.bgcolor, '&:hover':{ bgcolor:`${c.textMuted}10` } }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showThreats?'#E4446E':c.textMuted} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><line x1="22" y1="12" x2="18" y2="12" /><line x1="6" y1="12" x2="2" y2="12" /><line x1="12" y1="6" x2="12" y2="2" /><line x1="12" y1="22" x2="12" y2="18" /></svg>
            <Typography sx={{ color:showThreats?'#E4446E':c.textMuted, fontSize:'0.95rem', fontWeight:600 }}>Threats</Typography>
          </Box>
        </Box>

        {moveIndex>0 && history[moveIndex-1] && <Box sx={cardSx}><Typography sx={labelSx}>Last Move</Typography><Box sx={{ display:'flex', alignItems:'center', gap:1.2, flexWrap:'wrap' }}>
          <Typography sx={{ color:c.textDark, fontSize:'1.25rem', fontWeight:800 }}>{history[moveIndex-1].san}</Typography>
          {classifyMove(history[moveIndex-1]).map((tag) => {
            const tagColor = tag==='Check'||tag==='Checkmate'?'#E4446E':tag==='Capture'?'#DE7345':tag==='Castle'?c.accent:tag==='Development'?'#00A5C4':c.textMuted;
            return <Box key={tag} sx={{ px:1.2, py:0.3, borderRadius:'8px', bgcolor:`${tagColor}12`, border:`1px solid ${tagColor}25` }}><Typography sx={{ color:tagColor, fontSize:'0.85rem', fontWeight:700 }}>{tag}</Typography></Box>;
          })}
        </Box></Box>}

        {isGameOver && <Box sx={{ ...cardSx, bgcolor:isCheckmate?`${c.accent}08`:`${c.textMuted}06`, border:isCheckmate?`1px solid ${c.accent}`:`1px solid ${c.textMuted}15` }}>
          <Typography sx={{ color:isCheckmate?c.accent:c.textDark, fontSize:'1.15rem', fontWeight:800, mb:0.3 }}>{isCheckmate?'Checkmate!':isStalemate?'Stalemate':isThreefoldRepetition?'Threefold Repetition':isInsufficientMaterial?'Insufficient Material':isFiftyMoveRule?'50-Move Rule':'Draw'}</Typography>
          <Typography sx={{ color:c.textMuted, fontSize:'1.05rem' }}>{isCheckmate?`${turnColor==='w'?'Black':'White'} wins the game.`:isStalemate?'Neither side can win — the game is drawn.':isThreefoldRepetition?'The same position occurred three times — draw.':isInsufficientMaterial?'Neither side has enough pieces to checkmate — draw.':isFiftyMoveRule?'50 moves without a capture or pawn move — draw.':'The game is drawn.'}</Typography>
        </Box>}
      </Box>
    </Box>
  );
}
